import { Button } from '@domgen/base-design-system-2.0/atoms/Button';
import { useEffect, useState } from 'react';
import { Modal } from '@domgen/base-design-system-2.0/molecules/Modal';
import { quoteModalContent } from './constants/quoteModalContent';
import { ChooseApplianceCareArticle } from './components/ChooseApplianceCareArticle';
import { chooseApplianceCareArticles } from './constants/chooseApplianceCareArticles';

import {
  formApplianceName,
  formCategoryName,
  store,
  useApplianceFormData,
  useAppState,
  useGaEventData,
  useQuote,
  useUrlParams,
} from '../../store/store';
import { StoreEvent } from '../../store/store.types';
import { QuoteResponse } from '../../models/services/api/postQuote';
import { OfferPanel } from './components/OfferPanel';
import { gaECommerceEvent, gaEvent, gaPageView } from '../../services/ga/ga';
import { formatLocalisedDate } from '@domgen/shared/utils/date/formatLocalisedDate';
import { usePostHog, useFeatureFlagEnabled } from '@posthog/react';
import { getSystemCode } from '../../utils/getSystemCode';
import { StepKey } from '../../enums/stepKey';
import { FormKey } from '../../enums/formKey';
import { PosthogFeatureFlags } from '@domgen/shared/enums/featureFlags';
import { getDiscountDisplayData } from '../../utils/discounts/getDiscountDisplayData';
import { parseLocalDateString } from '@domgen/shared/utils/date/parseLocalDateString';
import { calculateAgeInMonths } from '@domgen/shared/utils/date/calculateAgeInMonths';
import { BenefitsTableAccordion } from './components/BenefitsAccordionTable/BenefitsAccordionTable';
import { benefitsAccordionTableContent } from './constants/benefitsAccordionTableContent';
import { ApplianceCareTerms } from './components/ApplianceCareTerms';
import { OfferPanelA } from './components/OfferPanelA';
import { GiftIcon } from '../../assets/icons/GiftIcon';

const styles = {
  applianceCareTermsContainer: 'col-span-12 flex justify-center mb-space-400',
  container: 'flex flex-col items-center bg-bg-main',
  panel: 'grid grid-cols-12 max-w-[825px] p-space-500 mb-space-600 gap-space-600 w-full bg-white',
  headerRow: 'col-span-12 desktop:mr-space-400 desktop:mb-space-600',
  chooseApplianceCareContainer:
    'w-full col-span-12 flex flex-col justify-center items-center gap-space-500 px-space-700 pt-space-700 mb-space-800',
  chooseApplianceCareArticles:
    'flex flex-col desktop:flex-row justify-center items-center gap-space-500 desktop:mx-space-400 ',
  offerPanelModal: 'desktop:max-w-[355px]',
  buttonLayout: 'flex flex-col gap-space-500 desktop:flex-row desktop:justify-center',
  buttonLayoutModal:
    'flex flex-col w-full justify-center gap-space-500 desktop:flex-row desktop:items-center',
  buttonLayoutSideDraw: 'w-full flex flex-col gap-space-500 p-space-500 ',
};

export function QuotePageA() {
  const quote = useQuote();
  const { session } = useAppState();
  const formData = useApplianceFormData();
  const urlParams = useUrlParams();
  const systemCode = getSystemCode(urlParams?.rs);
  const [displayModal, setDisplayModal] = useState(false);
  const posthog = usePostHog();
  const quoteData = quote?.data as QuoteResponse;
  const gaEventData = useGaEventData() || {};
  const offerPrice = gaEventData?.netFirstPaymentAmount;
  const applianceName = formApplianceName.get() ?? '';
  const applianceCategory = formCategoryName.get() ?? '';
  const brand = import.meta.env.VITE_CLIENT_FRIENDLY_NAME;
  const appSessionID = session?.id;

  const purchaseDate = formData?.[StepKey.ARRIVAL_DATE]?.[FormKey.ARRIVAL_DATE];
  const show2MonthsPromotion =
    useFeatureFlagEnabled(PosthogFeatureFlags.TWO_MONTHS_PROP) &&
    !!purchaseDate &&
    calculateAgeInMonths(purchaseDate) < 3;

  useEffect(() => {
    gaPageView({
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
      appSessionID: session?.id,
      pagename: 'protection-details',
      offerPrice,
    });
  }, []);

  const discountTerms = getDiscountDisplayData(quoteData);

  const toggleModal = () => {
    setDisplayModal((prevDisplayModal) => {
      // Fire gaEvent only when the modal is being opened
      if (!prevDisplayModal) {
        gaEvent({
          event: 'genericGAEvent',
          eventCategory: 'appliance details form USA',
          eventAction: 'protectionDetails',
          eventLabel: 'No, I want to register with warranty only',
          pagename: 'protection-details',
          category: 'registration-pages',
          type: 'STANDARD',
          regSystemCode: systemCode,
          applianceCategory,
          applianceName,
          appSessionID,
          offerPrice,
        });

        gaPageView({
          event: 'spa-pageview',
          pagename: 'protection-details-popup',
          category: 'registration-pages',
          type: 'STANDARD',
          regSystemCode: systemCode,
          offerPrice,
          applianceName,
          applianceCategory,
          appSessionID,
        });

        gaEvent({
          event: 'genericGAEvent',
          eventCategory: 'appliance details form USA',
          eventAction: 'protectionDetails_popup_upsell',
          eventLabel: 'popup_presented',
          pagename: 'protection-details-popup',
          category: 'registration-pages',
          type: 'STANDARD',
          offerPrice,
          regSystemCode: systemCode,
          applianceCategory,
          applianceName,
          appSessionID,
        });
      }
      return !prevDisplayModal;
    });
  };

  const registerOnly = () => {
    store.send({ type: StoreEvent.REG_ONLY });
    //Register only event only continues from the modal
    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'protection_details_popup_upsell',
      eventLabel: 'No, register with warranty only',
      pagename: 'protection-details-popup',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory,
      applianceName,
      appSessionID,
      offerPrice,
    });

    setDisplayModal(false);
  };

  const continueToCheckout = () => {
    store.send({
      type: StoreEvent.CONTINUE_TO_CHECKOUT,
      data: { continueToCheckoutFromModal: displayModal },
    });

    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: displayModal ? 'protectionDetails_popup_upsell' : 'protectionDetails',
      eventLabel: 'Yes, I want this protection',
      pagename: displayModal ? 'protection-details-popup' : 'protection-details',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory,
      applianceName,
      appSessionID,
      offerPrice,
    });

    gaECommerceEvent({
      event: 'add_to_cart',
      pagename: displayModal ? 'protection-details-popup' : 'protection-details',
      regSystemCode: systemCode,
      version: 'WiB',
      currency: 'USD',
      appSessionID,
      ecommerce: {
        items: [
          {
            item_brand: brand,
            item_category: applianceCategory,
            appliance_type: applianceName,
            appliance_purchase_cost: Number(
              formData?.[StepKey.PURCHASE_PRICE]?.[FormKey.PURCHASE_PRICE],
            ),
            appliance_purchase_date: formatLocalisedDate(
              parseLocalDateString(formData?.[StepKey.ARRIVAL_DATE]?.[FormKey.ARRIVAL_DATE] || ''),
              'en-US',
            ),
            appliance_warranty: true,
            item_name: `${brand} ${applianceName}`,
            price: offerPrice,
            quantity: 1,
            quoteId: quoteData.items[0].id,
          },
        ],
      },
    });
  };

  const banner = () => {
    const discountTerm = discountTerms.discountTerm;
    const periodOfCover = discountTerms.periodOfCover;
    const discountPercentage = discountTerms.discountPercentage;
    const achMarker = discountTerms.discountPaymentType === 'ach' ? '*' : '';

    const formatBannerText = () => {
      if (show2MonthsPromotion) {
        return "Our Lowest Price Ever is Back - Don't Miss Out!";
      }

      if (discountTerms.discountPercentage && discountTerm === periodOfCover) {
        return `Online Exclusive Offer - Get ${discountPercentage}% off${achMarker}!`;
      }

      if (discountPercentage && discountTerm && discountTerm < periodOfCover) {
        return `Online Exclusive Offer - Get ${discountPercentage}% off for first ${discountTerm} months${achMarker}!`;
      }

      return <>ONLINE OFFER</>;
    };

    return (
      <div className="col-span-12" role="banner">
        <div className="flex flex-row w-full items-center justify-center py-space-400 px-space-600 rounded-radius-200 bg-success-100 text-center">
          <GiftIcon size={24} />
          <p className="ml-space-400 text-body-large-strong text-success-600">
            {formatBannerText()}
          </p>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.panel}>
          {banner()}
          <div className={styles.headerRow}>
            <h1 className="mb-space-400 text-h1">
              Your manufacturer's warranty doesn't cover everything - add extra coverage today
            </h1>
            <p className="text-body-large">
              Get extra coverage now and avoid unexpected repair costs.
            </p>
          </div>

          <div className="col-span-12 ">
            <BenefitsTableAccordion
              panels={benefitsAccordionTableContent}
              warrantyLength={quoteData?.items[0].guarantee ?? 12}
            />
          </div>

          <div className="col-span-12  md:col-span-6">
            <div className="bg-bg-primary px-space-600 py-space-500 rounded-radius-200 w-full">
              <ApplianceCareTerms
                href={import.meta.env.VITE_APPLIANCE_CARE_TERMS_AND_CONDITIONS_PATH}
                brand={import.meta.env.VITE_CLIENT_FRIENDLY_NAME}
              />
            </div>
          </div>

          <div className="col-span-12  md:col-span-6">
            <OfferPanelA
              periodOfCover={discountTerms.periodOfCover}
              discountPercentage={discountTerms.discountPercentage}
              discountTerm={discountTerms.discountTerm}
              netFirstPaymentAmount={discountTerms.netFirstPaymentAmount}
              netFirstPaymentAmountAfterPromotionalPeriod={
                discountTerms.netFirstPaymentAmountAfterPromotionalPeriod
              }
              discountPaymentType={discountTerms.discountPaymentType}
              show2MonthsPromotion={show2MonthsPromotion}
            />
          </div>

          <div className="col-span-12 flex flex-row gap-space-300 md:row-reverse">
            <Button
              variant="outline"
              onClick={() => {
                posthog.capture('protection-details-page-register-only-cta-clicked');
                toggleModal();
              }}
            >
              No, register only
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                posthog.capture('protection-details-page-monthly-upsell-cta-clicked');
                continueToCheckout();
              }}
            >
              Yes, protect my appliance!
            </Button>
          </div>
        </div>
      </div>

      <div className={styles.chooseApplianceCareContainer}>
        <h5 className="text-center text-h5 desktop:text-left">
          Why choose {brand} Appliance Care?
        </h5>

        <div className={styles.chooseApplianceCareArticles}>
          {chooseApplianceCareArticles.map((article, index) => (
            <ChooseApplianceCareArticle
              icon={article.icon}
              description={article.description}
              key={index}
            />
          ))}
        </div>
      </div>

      <Modal isOpen={displayModal} onClose={toggleModal}>
        <h1 className="text-h5  mb-space-400">{quoteModalContent.title}</h1>
        <p className="mb-space-400">{quoteModalContent.description}</p>
        <p className="text-body-strong mb-space-200">{quoteModalContent.benefits.heading}</p>
        <ul className="list-none mb-4 text-left">
          {quoteModalContent.benefits.benefitsList.map((benefit, index) => (
            <li key={index} className="flex items-center mb-2">
              <span className="mr-2">{<benefit.icon />}</span>
              <span>{benefit.text}</span>
            </li>
          ))}
        </ul>
        <div className="flex flex-col items-center gap-space-400 ">
          <div className={styles.offerPanelModal}>
            <OfferPanel
              periodOfCover={discountTerms.periodOfCover}
              discountPercentage={discountTerms.discountPercentage}
              discountTerm={discountTerms.discountTerm}
              netFirstPaymentAmount={discountTerms.netFirstPaymentAmount}
              netFirstPaymentAmountAfterPromotionalPeriod={
                discountTerms.netFirstPaymentAmountAfterPromotionalPeriod
              }
              discountPaymentType={discountTerms.discountPaymentType}
              show2MonthsPromotion={show2MonthsPromotion}
            />
          </div>

          <div className={styles.buttonLayoutModal}>
            <Button
              variant="outline"
              onClick={() => {
                registerOnly();
                posthog.capture('protection-details-modal-register-only-cta-clicked');
              }}
            >
              No thanks, register only
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                continueToCheckout();
                posthog.capture('protection-details-modal-monthly-upsell-cta-clicked');
              }}
            >
              Register with extra coverage
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
